/**
 * The things My account's Security card can actually do —
 * `design-remediation.md` WP2, card 2.
 *
 * Each row's button used to be a row: "Password · changed Jun 14" with nowhere
 * to go. These are the flows behind them, in the same `EditorShell` every
 * other popup in the product uses, so the account page stays a page of rows
 * and the forms live somewhere a second screen can reach them.
 *
 * The password one is `POST /me/password`, because `/auth/set-password` wants
 * a token from an emailed link, which is the flow for somebody locked out. The
 * crew PIN is the existing `PUT /people/:id/pin` — a person may always set
 * their own.
 */
import { useState } from 'react'
import { PASSWORD_MIN_LENGTH, type MeResponse } from '@rowted/shared'
import { EditorShell } from '../quote/quote-editors.js'
import { Band, Button } from '../components/primitives.js'
import { Field } from '../components/fields.js'
import { request } from '../lib/api.js'

/*
 * Two-step is not one of them any more: it is a list of methods, set up in
 * `TwoStepSetup` (`components/two-step-setup.tsx`). The authenticator-only
 * editor that used to be here posted to `/auth/mfa/enroll`, which answers 410
 * since the move to methods, and nothing opened it — so it went (step 27)
 * rather than stay as the obvious name for the next person to reach for.
 */
export type SecurityAction = 'password' | 'pin'

export function AccountSecurityEditor({ action, me, anchor, onClose, onDone }: {
  action: SecurityAction | null
  me: MeResponse
  anchor: HTMLElement | null
  onClose: () => void
  onDone: (message: string) => void
}) {
  if (action === 'password') {
    return <PasswordEditor anchor={anchor} onClose={onClose} onDone={onDone} />
  }
  if (action === 'pin') {
    return <PinEditor me={me} anchor={anchor} onClose={onClose} onDone={onDone} />
  }
  return null
}

interface EditorProps {
  anchor: HTMLElement | null
  onClose: () => void
  onDone: (message: string) => void
}

function PasswordEditor({ anchor, onClose, onDone }: EditorProps) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const ready = current.length > 0 && next.length >= PASSWORD_MIN_LENGTH && next === again

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      const body = await request<{ revoked: number }>(
        '/me/password', { method: 'POST', body: { current, password: next } })
      onDone(body.revoked > 0
        ? `Password changed, and ${body.revoked} other `
          + `${body.revoked === 1 ? 'session was' : 'sessions were'} signed out.`
        : 'Password changed.')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That did not work.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <EditorShell
      open
      onClose={onClose}
      anchor={anchor}
      label="Change your password"
      width={420}
      footer={(
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!ready || busy} onClick={() => void submit()}>
            {busy ? 'Changing…' : 'Change password'}
          </Button>
        </>
      )}
    >
      <div className="flex flex-col gap-3">
        <Field label="Your password now" value={current} onChange={setCurrent}
          type="password" autoFocus />
        <Field label="New password" value={next} onChange={setNext} type="password"
          hint={`At least ${PASSWORD_MIN_LENGTH} characters.`} />
        <Field label="New password again" value={again} onChange={setAgain} type="password" />
        {again.length > 0 && next !== again && (
          <Band tone="amber">Those two do not match.</Band>
        )}
        <Band tone="teal">
          Every other device you are signed in on is signed out — which is the point,
          if somebody else may have had the old one.
        </Band>
        {error && <Band tone="amber">{error}</Band>}
      </div>
    </EditorShell>
  )
}

/**
 * Your own crew PIN.
 *
 * The same `PUT /people/:id/pin` a lead uses to reset somebody's — a person
 * may always set their own, and routing it through a second endpoint would be
 * a second place for the length rule to live.
 */
function PinEditor({ me, anchor, onClose, onDone }: EditorProps & { me: MeResponse }) {
  const [pin, setPin] = useState('')
  const [again, setAgain] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const ready = /^\d{4,6}$/.test(pin) && pin === again

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      await request(`/people/${me.user.id}/pin`, { method: 'PUT', body: { pin } })
      onDone('Your crew PIN is set.')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That did not work.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <EditorShell
      open onClose={onClose} anchor={anchor} label="Your crew PIN" width={380}
      footer={(
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!ready || busy} onClick={() => void submit()}>
            {busy ? 'Saving…' : 'Set the PIN'}
          </Button>
        </>
      )}
    >
      <div className="flex flex-col gap-3">
        <p className="m-0 text-cell text-text-muted">
          Four to six digits, typed on the crew iPad to clock in. It is not your
          password and it only works from a registered device.
        </p>
        <Field label="PIN" value={pin}
          onChange={(value) => setPin(value.replace(/\D/g, '').slice(0, 6))}
          type="password" autoFocus />
        <Field label="PIN again" value={again}
          onChange={(value) => setAgain(value.replace(/\D/g, '').slice(0, 6))}
          type="password" />
        {again.length > 0 && pin !== again && <Band tone="amber">Those two do not match.</Band>}
        {error && <Band tone="amber">{error}</Band>}
      </div>
    </EditorShell>
  )
}
